import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import type {
    Account,
    PostingCorrectionBatchDetail,
    PostingCorrectionBatchSummary,
    PostingCorrectionCandidate,
    PostingCorrectionDetail,
    PostingCorrectionPatch,
    PostingCorrectionPreview,
    PostingCorrectionSourceType,
} from '../../services/types';


const SOURCE_LABELS: Record<PostingCorrectionSourceType, string> = {
    AR_RECEIPT: 'Customer Receipt',
    AP_PAYMENT: 'Supplier Payment',
    AR_PDC: 'Customer PDC',
    AP_PDC: 'Supplier PDC',
    MANUAL_JOURNAL: 'Manual Journal',
};

type DetailDraft = {
    journal_date: string;
    adjustment_date: string;
    bank_account_id: string;
    reference: string;
    description: string;
    amount: string;
    instrument_no: string;
    allocations: Array<{ document_id: string; apply_amount: string }>;
    journal_lines: Array<{ line_id?: number | null; account_id: string; debit: string; credit: string; reference: string }>;
};

function money(value: unknown): string {
    return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function dateOnly(value: unknown): string {
    return String(value || '').slice(0, 10);
}

function buildDetailDraft(detail: PostingCorrectionDetail): DetailDraft {
    const snapshot = detail.snapshot || {};
    const settlement = snapshot.settlement || {};
    const pdc = snapshot.pdc || {};
    return {
        journal_date: dateOnly(detail.journal_date),
        adjustment_date: new Date().toISOString().slice(0, 10),
        bank_account_id: detail.bank_account_id ? String(detail.bank_account_id) : '',
        reference: String(settlement.reference ?? pdc.reference ?? ''),
        description: String(snapshot.journal?.description || ''),
        amount: String(settlement.amount ?? pdc.amount ?? detail.amount ?? ''),
        instrument_no: String(pdc.instrument_no || ''),
        allocations: (settlement.allocations || []).map((line: any) => ({
            document_id: String(line.document_id || ''),
            apply_amount: String(line.apply_amount || ''),
        })),
        journal_lines: (snapshot.journal?.lines || []).map((line: any) => ({
            line_id: line.line_id,
            account_id: String(line.account_id || ''),
            debit: String(line.debit || 0),
            credit: String(line.credit || 0),
            reference: String(line.reference || ''),
        })),
    };
}

export function PostingCorrections() {
    const { hasPermission } = useAuth();
    const [candidates, setCandidates] = useState<PostingCorrectionCandidate[]>([]);
    const [candidateTotal, setCandidateTotal] = useState(0);
    const [candidateOffset, setCandidateOffset] = useState(0);
    const [history, setHistory] = useState<PostingCorrectionBatchSummary[]>([]);
    const [historyDetail, setHistoryDetail] = useState<PostingCorrectionBatchDetail | null>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [query, setQuery] = useState('');
    const [sourceType, setSourceType] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [reason, setReason] = useState('');
    const [bulkDate, setBulkDate] = useState('');
    const [bulkAdjustmentDate, setBulkAdjustmentDate] = useState(new Date().toISOString().slice(0, 10));
    const [bulkBank, setBulkBank] = useState('');
    const [bulkReference, setBulkReference] = useState('');
    const [bulkDescription, setBulkDescription] = useState('');
    const [detail, setDetail] = useState<PostingCorrectionDetail | null>(null);
    const [detailDraft, setDetailDraft] = useState<DetailDraft | null>(null);
    const [preview, setPreview] = useState<PostingCorrectionPreview | null>(null);
    const [confirmation, setConfirmation] = useState('');
    const [busy, setBusy] = useState(false);

    const canApply = hasPermission('finance.corrections.apply');
    const canUndo = hasPermission('finance.corrections.undo');

    const pageSize = 50;

    const refresh = async (nextOffset: number = candidateOffset) => {
        setBusy(true);
        try {
            const [candidatePayload, accountRows, historyPayload] = await Promise.all([
                api.financials.listPostingCorrectionCandidates({
                    source_type: sourceType || undefined,
                    from: fromDate || undefined,
                    to: toDate || undefined,
                    q: query || undefined,
                    limit: pageSize,
                    offset: nextOffset,
                }),
                api.financials.listAccounts(),
                api.financials.listPostingCorrectionBatches(),
            ]);
            setCandidates(candidatePayload.items || []);
            setCandidateTotal(candidatePayload.total || 0);
            setCandidateOffset(nextOffset);
            setAccounts(accountRows.filter((row) => row.is_active !== false && row.is_postable !== false));
            setHistory(historyPayload.items || []);
            setSelected(new Set());
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load posting corrections');
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        void refresh();
    }, []);

    const selectedRows = useMemo(
        () => candidates.filter((row) => selected.has(`${row.source_type}:${row.source_id}`)),
        [candidates, selected],
    );
    const bankAccounts = useMemo(
        () => accounts.filter((row) => {
            const label = `${row.code || ''} ${row.name || ''}`.toLowerCase();
            return row.type === 'Asset' && (label.includes('cash') || label.includes('bank'));
        }),
        [accounts],
    );
    const anyClosed = selectedRows.some((row) => !row.period.is_open);

    const toggle = (row: PostingCorrectionCandidate) => {
        const key = `${row.source_type}:${row.source_id}`;
        setSelected((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
        setPreview(null);
    };

    const openDetail = async (row: PostingCorrectionCandidate) => {
        setBusy(true);
        try {
            const payload = await api.financials.getPostingCorrectionDetail(row.source_type, row.source_id);
            setDetail(payload);
            setDetailDraft(buildDetailDraft(payload));
            setSelected(new Set([`${row.source_type}:${row.source_id}`]));
            setPreview(null);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load transaction detail');
        } finally {
            setBusy(false);
        }
    };

    const detailPatch = (): PostingCorrectionPatch => {
        if (!detail || !detailDraft) return {};
        const snapshot = detail.snapshot || {};
        const settlement = snapshot.settlement || {};
        const pdc = snapshot.pdc || {};
        const patch: PostingCorrectionPatch = {};
        if (detail.period.is_open) {
            if (detailDraft.journal_date !== dateOnly(detail.journal_date)) patch.journal_date = detailDraft.journal_date;
            if (detailDraft.reference !== String(settlement.reference ?? pdc.reference ?? '')) patch.reference = detailDraft.reference;
            if (detailDraft.description !== String(snapshot.journal?.description || '')) patch.description = detailDraft.description;
            if (detailDraft.amount && Number(detailDraft.amount) !== Number(settlement.amount ?? pdc.amount ?? detail.amount)) {
                patch.amount = Number(detailDraft.amount);
            }
            if (pdc.instrument_no !== undefined && detailDraft.instrument_no !== String(pdc.instrument_no || '')) {
                patch.instrument_no = detailDraft.instrument_no;
            }
            const originalAllocations = JSON.stringify((settlement.allocations || []).map((line: any) => ({
                document_id: Number(line.document_id), apply_amount: Number(line.apply_amount),
            })));
            const nextAllocations = detailDraft.allocations
                .filter((line) => line.document_id && Number(line.apply_amount) > 0)
                .map((line) => ({ document_id: Number(line.document_id), apply_amount: Number(line.apply_amount) }));
            if (JSON.stringify(nextAllocations) !== originalAllocations) patch.allocations = nextAllocations;
            if (detail.source_type === 'MANUAL_JOURNAL') {
                const originalLines = JSON.stringify((snapshot.journal?.lines || []).map((line: any) => ({
                    line_id: line.line_id, account_id: Number(line.account_id), debit: Number(line.debit),
                    credit: Number(line.credit), reference: String(line.reference || ''),
                })));
                const nextLines = detailDraft.journal_lines.map((line) => ({
                    line_id: line.line_id, account_id: Number(line.account_id), debit: Number(line.debit || 0),
                    credit: Number(line.credit || 0), reference: line.reference,
                }));
                if (JSON.stringify(nextLines) !== originalLines) patch.journal_lines = nextLines;
            }
        } else {
            patch.adjustment_date = detailDraft.adjustment_date;
        }
        if (
            (!detail.period.is_open || detail.source_type !== 'MANUAL_JOURNAL')
            && detailDraft.bank_account_id
            && Number(detailDraft.bank_account_id) !== Number(detail.bank_account_id || 0)
        ) {
            patch.bank_account_id = Number(detailDraft.bank_account_id);
        }
        return patch;
    };

    const previewChanges = async () => {
        if (!reason.trim()) {
            toast.error('Enter a correction reason.');
            return;
        }
        if (selectedRows.length === 0) {
            toast.error('Select at least one transaction.');
            return;
        }
        let items: Array<{ source_type: PostingCorrectionSourceType; source_id: number; changes: PostingCorrectionPatch }>;
        if (detail && detailDraft && selectedRows.length === 1 && detail.source_id === selectedRows[0].source_id && detail.source_type === selectedRows[0].source_type) {
            items = [{ source_type: detail.source_type, source_id: detail.source_id, changes: detailPatch() }];
        } else {
            items = selectedRows.map((row) => {
                const changes: PostingCorrectionPatch = {};
                if (bulkBank) changes.bank_account_id = Number(bulkBank);
                if (row.period.is_open) {
                    if (bulkDate) changes.journal_date = bulkDate;
                    if (bulkReference) changes.reference = bulkReference;
                    if (bulkDescription) changes.description = bulkDescription;
                } else {
                    changes.adjustment_date = bulkAdjustmentDate;
                }
                return { source_type: row.source_type, source_id: row.source_id, changes };
            });
        }
        if (items.some((item) => Object.keys(item.changes).length === 0)) {
            toast.error('Enter at least one corrected value for every selected transaction.');
            return;
        }
        setBusy(true);
        try {
            const result = await api.financials.previewPostingCorrections({ reason: reason.trim(), items });
            setPreview(result);
            setConfirmation('');
        } catch (error: any) {
            toast.error(error?.message || 'Correction preview failed');
        } finally {
            setBusy(false);
        }
    };

    const applyPreview = async () => {
        if (!preview) return;
        setBusy(true);
        try {
            await api.financials.applyPostingCorrectionBatch(preview.batch_id, confirmation);
            toast.success('Posting correction batch applied with a complete audit record.');
            setPreview(null);
            setDetail(null);
            setDetailDraft(null);
            setReason('');
            await refresh();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to apply correction batch');
        } finally {
            setBusy(false);
        }
    };

    const undo = async (batchId: number) => {
        setBusy(true);
        try {
            await api.financials.undoPostingCorrectionBatch(batchId);
            toast.success(`Correction batch ${batchId} was undone.`);
            await refresh();
        } catch (error: any) {
            toast.error(error?.message || 'Unable to undo correction batch');
        } finally {
            setBusy(false);
        }
    };

    const viewEvidence = async (batchId: number) => {
        setBusy(true);
        try {
            setHistoryDetail(await api.financials.getPostingCorrectionBatch(batchId));
        } catch (error: any) {
            toast.error(error?.message || 'Unable to load correction evidence');
        } finally {
            setBusy(false);
        }
    };

    const importCsv = async (file?: File) => {
        if (!file) return;
        if (!reason.trim()) {
            toast.error('Enter the batch reason before importing CSV.');
            return;
        }
        setBusy(true);
        try {
            const result = await api.financials.previewPostingCorrectionCsv(reason.trim(), await file.text());
            setPreview(result);
            setConfirmation('');
        } catch (error: any) {
            toast.error(error?.message || 'CSV preview failed');
        } finally {
            setBusy(false);
        }
    };

    if (!hasPermission('finance.corrections.view')) {
        return <Card><CardContent className="p-6 text-sm text-muted-foreground">You do not have permission to view posting corrections.</CardContent></Card>;
    }

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle>Controlled Posting Corrections</CardTitle>
                    <p className="text-sm text-muted-foreground">
                        Correct bank-linked postings with preview, immutable before/after evidence, automatic backup, and controlled undo. Nothing is deleted.
                    </p>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid gap-3 md:grid-cols-5">
                        <input className="rounded-md border bg-background px-3 py-2 text-sm" placeholder="Journal, party, reference…" value={query} onChange={(e) => setQuery(e.target.value)} />
                        <select className="rounded-md border bg-background px-3 py-2 text-sm" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                            <option value="">All source types</option>
                            {Object.entries(SOURCE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                        <input className="rounded-md border bg-background px-3 py-2 text-sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                        <input className="rounded-md border bg-background px-3 py-2 text-sm" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                        <Button type="button" onClick={() => void refresh(0)} disabled={busy}>Refresh</Button>
                    </div>
                    {candidates.some((row) => !row.period.configured) && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                            Period controls are not configured. Those transactions are treated as open and this condition will be recorded in every correction audit.
                        </div>
                    )}
                    <div className="max-h-[440px] overflow-auto rounded-md border">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-muted text-left"><tr>
                                <th className="p-2">Select</th><th className="p-2">Date</th><th className="p-2">Journal</th>
                                <th className="p-2">Source / Party</th><th className="p-2 text-right">Amount</th><th className="p-2">Period</th><th className="p-2">Action</th>
                            </tr></thead>
                            <tbody>
                                {candidates.map((row) => {
                                    const key = `${row.source_type}:${row.source_id}`;
                                    return <tr key={key} className="border-t align-top">
                                        <td className="p-2"><input aria-label={`Select ${row.journal_no}`} type="checkbox" checked={selected.has(key)} onChange={() => toggle(row)} /></td>
                                        <td className="p-2 whitespace-nowrap">{row.journal_date}</td>
                                        <td className="p-2 font-medium">{row.journal_no}<div className="max-w-xs truncate text-xs text-muted-foreground">{row.description}</div></td>
                                        <td className="p-2">{SOURCE_LABELS[row.source_type]}<div className="text-xs text-muted-foreground">{row.party_name}</div></td>
                                        <td className="p-2 text-right">{money(row.amount)}</td>
                                        <td className="p-2"><span className={`rounded px-2 py-1 text-xs ${row.period.is_open ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`}>{row.period.state}</span></td>
                                        <td className="p-2"><Button type="button" variant="outline" size="sm" onClick={() => void openDetail(row)}>Edit details</Button></td>
                                    </tr>;
                                })}
                                {!candidates.length && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No eligible bank-linked postings found.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{candidateTotal ? `${candidateOffset + 1}–${Math.min(candidateOffset + candidates.length, candidateTotal)} of ${candidateTotal}` : '0 transactions'}</span>
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" size="sm" disabled={busy || candidateOffset === 0} onClick={() => void refresh(Math.max(0, candidateOffset - pageSize))}>Previous</Button>
                            <Button type="button" variant="outline" size="sm" disabled={busy || candidateOffset + candidates.length >= candidateTotal} onClick={() => void refresh(candidateOffset + pageSize)}>Next</Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {canApply && (
                <Card>
                    <CardHeader><CardTitle>Prepare Batch ({selectedRows.length} selected)</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                        <label className="block text-sm font-medium">Required correction reason
                            <textarea className="mt-1 w-full rounded-md border bg-background px-3 py-2" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain the reconciliation evidence and why the original posting is being corrected." />
                        </label>
                        {!detailDraft && <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
                            <label className="text-sm">Corrected date<input disabled={anyClosed} className="mt-1 w-full rounded-md border bg-background px-3 py-2 disabled:opacity-50" type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} /></label>
                            <label className="text-sm">Adjustment date<input className="mt-1 w-full rounded-md border bg-background px-3 py-2" type="date" value={bulkAdjustmentDate} onChange={(e) => setBulkAdjustmentDate(e.target.value)} /></label>
                            <label className="text-sm">Replacement bank<select className="mt-1 w-full rounded-md border bg-background px-3 py-2" value={bulkBank} onChange={(e) => setBulkBank(e.target.value)}><option value="">No bulk change</option>{bankAccounts.map((row) => <option key={row.id} value={row.id}>{row.code} · {row.name}</option>)}</select></label>
                            <label className="text-sm">Reference<input disabled={anyClosed} className="mt-1 w-full rounded-md border bg-background px-3 py-2 disabled:opacity-50" value={bulkReference} onChange={(e) => setBulkReference(e.target.value)} /></label>
                            <label className="text-sm">Description<input disabled={anyClosed} className="mt-1 w-full rounded-md border bg-background px-3 py-2 disabled:opacity-50" value={bulkDescription} onChange={(e) => setBulkDescription(e.target.value)} /></label>
                        </div>}
                        {detail && detailDraft && <DetailEditor detail={detail} draft={detailDraft} setDraft={setDetailDraft} accounts={accounts} bankAccounts={bankAccounts} onClose={() => { setDetail(null); setDetailDraft(null); }} />}
                        <div className="flex flex-wrap gap-2">
                            <Button type="button" onClick={() => void previewChanges()} disabled={busy || !selectedRows.length}>Preview impact</Button>
                            <Button type="button" variant="outline" onClick={() => void api.financials.exportPostingCorrectionCsv()}>Export CSV template</Button>
                            <label className="inline-flex cursor-pointer items-center rounded-md border px-3 py-2 text-sm font-medium">Import CSV<input className="hidden" type="file" accept=".csv,text/csv" onChange={(e) => void importCsv(e.target.files?.[0])} /></label>
                        </div>
                    </CardContent>
                </Card>
            )}

            {preview && <Card className="border-amber-300">
                <CardHeader><CardTitle>Preview Batch #{preview.batch_id}</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                    <div className="rounded-md bg-muted p-3 text-sm">{preview.item_count} transaction(s) validated. A server backup and a final stale-record check will run before any changes are committed.</div>
                    <ul className="space-y-1 text-sm">{preview.items.map((item: any) => <li key={`${item.source_type}:${item.source_id}`}>{item.journal_no} — {item.strategy.replaceAll('_', ' ')}</li>)}</ul>
                    <label className="block text-sm font-medium">Type <code>{preview.confirmation_required}</code>
                        <input className="mt-1 w-full rounded-md border bg-background px-3 py-2 font-mono" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
                    </label>
                    <Button type="button" onClick={() => void applyPreview()} disabled={busy || confirmation !== preview.confirmation_required}>Apply controlled corrections</Button>
                </CardContent>
            </Card>}

            <Card>
                <CardHeader><CardTitle>Correction History</CardTitle></CardHeader>
                <CardContent><div className="space-y-2">
                    {history.map((batch) => <div key={batch.batch_id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm">
                        <div><strong>Batch #{batch.batch_id}</strong> · {batch.item_count} item(s) · {batch.status}<div className="text-muted-foreground">{batch.reason} — {batch.created_by_username || 'Unknown'} at {batch.created_at}</div></div>
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" size="sm" onClick={() => void viewEvidence(batch.batch_id)} disabled={busy}>View evidence</Button>
                            {canUndo && batch.status === 'APPLIED' && <Button type="button" variant="outline" size="sm" onClick={() => void undo(batch.batch_id)} disabled={busy}>Undo batch</Button>}
                        </div>
                    </div>)}
                    {!history.length && <p className="text-sm text-muted-foreground">No correction batches have been created.</p>}
                </div></CardContent>
            </Card>
            {historyDetail && <Card>
                <CardHeader><div className="flex items-center justify-between"><CardTitle>Batch #{historyDetail.batch_id} Evidence</CardTitle><Button type="button" variant="ghost" size="sm" onClick={() => setHistoryDetail(null)}>Close</Button></div></CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-sm">{historyDetail.reason} · {historyDetail.status} · {historyDetail.created_by_username || 'Unknown actor'}</p>
                    {historyDetail.items.map((item) => <details key={item.item_id} className="rounded-md border p-3">
                        <summary className="cursor-pointer text-sm font-medium">{item.source_type} #{item.source_id} · {item.strategy.replaceAll('_', ' ')}</summary>
                        <div className="mt-3 grid gap-3 lg:grid-cols-2">
                            <div><p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Before</p><pre className="max-h-96 overflow-auto rounded bg-muted p-3 text-xs">{JSON.stringify(item.before, null, 2)}</pre></div>
                            <div><p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">After</p><pre className="max-h-96 overflow-auto rounded bg-muted p-3 text-xs">{JSON.stringify(item.after, null, 2)}</pre></div>
                        </div>
                    </details>)}
                </CardContent>
            </Card>}
        </div>
    );
}

function DetailEditor({ detail, draft, setDraft, accounts, bankAccounts, onClose }: {
    detail: PostingCorrectionDetail;
    draft: DetailDraft;
    setDraft: (draft: DetailDraft) => void;
    accounts: Account[];
    bankAccounts: Account[];
    onClose: () => void;
}) {
    const isManual = detail.source_type === 'MANUAL_JOURNAL';
    const lockedAmounts = detail.has_downstream_prepayment || !detail.period.is_open;
    return <div className="space-y-3 rounded-md border p-4">
        <div className="flex items-center justify-between"><strong>{detail.journal_no} detail correction</strong><Button type="button" variant="ghost" size="sm" onClick={onClose}>Close details</Button></div>
        {!detail.period.is_open && <p className="rounded bg-amber-50 p-2 text-sm text-amber-900">This period is {detail.period.state}. Only a current-period bank reclassification is permitted.</p>}
        {detail.has_downstream_prepayment && <p className="rounded bg-amber-50 p-2 text-sm text-amber-900">Amount and allocations are locked because this prepayment has downstream allocation journals.</p>}
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <label className="text-sm">{detail.period.is_open ? 'Corrected date' : 'Adjustment date'}<input className="mt-1 w-full rounded-md border bg-background px-3 py-2" type="date" value={detail.period.is_open ? draft.journal_date : draft.adjustment_date} onChange={(e) => setDraft({ ...draft, [detail.period.is_open ? 'journal_date' : 'adjustment_date']: e.target.value })} /></label>
            {(!isManual || !detail.period.is_open) && <label className="text-sm">Cash/Bank account<select className="mt-1 w-full rounded-md border bg-background px-3 py-2" value={draft.bank_account_id} onChange={(e) => setDraft({ ...draft, bank_account_id: e.target.value })}><option value="">Select bank</option>{bankAccounts.map((row) => <option key={row.id} value={row.id}>{row.code} · {row.name}</option>)}</select></label>}
            {!isManual && <label className="text-sm">Amount<input disabled={lockedAmounts} className="mt-1 w-full rounded-md border bg-background px-3 py-2 disabled:opacity-50" type="number" step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} /></label>}
            {!isManual && <label className="text-sm">Reference<input disabled={!detail.period.is_open} className="mt-1 w-full rounded-md border bg-background px-3 py-2 disabled:opacity-50" value={draft.reference} onChange={(e) => setDraft({ ...draft, reference: e.target.value })} /></label>}
            <label className="text-sm lg:col-span-2">Description<input disabled={!detail.period.is_open} className="mt-1 w-full rounded-md border bg-background px-3 py-2 disabled:opacity-50" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></label>
            {detail.snapshot?.pdc && <label className="text-sm">Cheque / instrument no.<input disabled={!detail.period.is_open} className="mt-1 w-full rounded-md border bg-background px-3 py-2 disabled:opacity-50" value={draft.instrument_no} onChange={(e) => setDraft({ ...draft, instrument_no: e.target.value })} /></label>}
        </div>
        {!isManual && detail.period.is_open && <div><div className="mb-2 flex items-center justify-between"><strong className="text-sm">Allocations</strong><Button type="button" size="sm" variant="outline" disabled={lockedAmounts} onClick={() => setDraft({ ...draft, allocations: [...draft.allocations, { document_id: '', apply_amount: '' }] })}>Add allocation</Button></div>
            <div className="space-y-2">{draft.allocations.map((line, index) => <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2"><input disabled={lockedAmounts} className="rounded-md border bg-background px-3 py-2 text-sm" placeholder="Invoice/Bill ID" value={line.document_id} onChange={(e) => { const next = [...draft.allocations]; next[index] = { ...line, document_id: e.target.value }; setDraft({ ...draft, allocations: next }); }} /><input disabled={lockedAmounts} className="rounded-md border bg-background px-3 py-2 text-sm" type="number" step="0.01" placeholder="Amount" value={line.apply_amount} onChange={(e) => { const next = [...draft.allocations]; next[index] = { ...line, apply_amount: e.target.value }; setDraft({ ...draft, allocations: next }); }} /><Button type="button" variant="ghost" disabled={lockedAmounts} onClick={() => setDraft({ ...draft, allocations: draft.allocations.filter((_, i) => i !== index) })}>Remove</Button></div>)}</div>
        </div>}
        {isManual && detail.period.is_open && <div><div className="mb-2 flex items-center justify-between"><strong className="text-sm">Journal lines</strong><Button type="button" size="sm" variant="outline" onClick={() => setDraft({ ...draft, journal_lines: [...draft.journal_lines, { account_id: '', debit: '0', credit: '0', reference: '' }] })}>Add line</Button></div>
            <div className="space-y-2">{draft.journal_lines.map((line, index) => <div key={line.line_id || `new-${index}`} className="grid gap-2 md:grid-cols-[2fr_1fr_1fr_2fr_auto]"><select className="rounded-md border bg-background px-2 py-2 text-sm" value={line.account_id} onChange={(e) => { const next = [...draft.journal_lines]; next[index] = { ...line, account_id: e.target.value }; setDraft({ ...draft, journal_lines: next }); }}><option value="">Account</option>{accounts.map((row) => <option key={row.id} value={row.id}>{row.code} · {row.name}</option>)}</select><input className="rounded-md border bg-background px-2 py-2 text-sm" type="number" step="0.01" value={line.debit} onChange={(e) => { const next = [...draft.journal_lines]; next[index] = { ...line, debit: e.target.value }; setDraft({ ...draft, journal_lines: next }); }} /><input className="rounded-md border bg-background px-2 py-2 text-sm" type="number" step="0.01" value={line.credit} onChange={(e) => { const next = [...draft.journal_lines]; next[index] = { ...line, credit: e.target.value }; setDraft({ ...draft, journal_lines: next }); }} /><input className="rounded-md border bg-background px-2 py-2 text-sm" placeholder="Reference" value={line.reference} onChange={(e) => { const next = [...draft.journal_lines]; next[index] = { ...line, reference: e.target.value }; setDraft({ ...draft, journal_lines: next }); }} /><Button type="button" variant="ghost" onClick={() => setDraft({ ...draft, journal_lines: draft.journal_lines.filter((_, i) => i !== index) })}>Remove</Button></div>)}</div>
        </div>}
    </div>;
}
